import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Check, Sparkles, Filter, Users, FileSearch, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePricing } from "@/lib/use-pricing";

const features = [
  {
    icon: FileSearch,
    title: "Attorney-ready case files",
    desc: "Every case arrives with executive summary, timeline, key facts, damages, parties, and uploaded evidence.",
  },
  {
    icon: Filter,
    title: "Match by jurisdiction & practice",
    desc: "Only see cases that match your licensed states and practice areas. No noise.",
  },
  {
    icon: Sparkles,
    title: "Urgency & completeness scoring",
    desc: "AI scores every case so you know at a glance which to engage with first.",
  },
  {
    icon: Users,
    title: "Client contact information",
    desc: "Active subscribers get full client contact details — name, email, phone, city, state.",
  },
];

export default function AttorneysPage() {
  const { plans, loading: plansLoading, error: plansError } = usePricing();
  const seoDescription = plans.length
    ? `Pre-screened, jurisdiction-matched legal leads. Monthly pricing: ${plans
        .map((p) => `${p.name} $${p.monthly_amount_usd}`)
        .join(", ")}. Free demo available.`
    : "Pre-screened, jurisdiction-matched legal leads. Subscribe monthly with transparent pricing — free demo available.";

  return (
    <div data-testid="attorneys-page">
      <Seo
        title="For Attorneys & Law Firms — Qualified leads on LexiNetwork"
        description={seoDescription}
        path="/attorneys"
      />
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
              For Attorneys & Law Firms
            </p>
            <h1 className="font-heading text-5xl lg:text-6xl font-bold text-[#0A192F] tracking-tight leading-[1.05]">
              Pre-qualified cases.{" "}
              <span className="font-serif-display italic font-normal">
                Delivered.
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Stop wading through intake calls. Subscribe to LexIntake and get
              instant access to attorney-ready case files matching your
              practice areas and licensed states.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  data-testid="attorneys-register-btn"
                  size="lg"
                  className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-7 py-6 text-base w-full sm:w-auto"
                >
                  Apply for Approval
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  data-testid="attorneys-login-btn"
                  size="lg"
                  variant="outline"
                  className="rounded-lg px-7 py-6 text-base border-slate-300 text-[#0A192F] w-full sm:w-auto"
                >
                  Attorney Login
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {features.map((f) => (
                <div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="p-5 bg-[#F8FAFC] rounded-xl border border-slate-200"
                >
                  <span className="w-10 h-10 rounded-lg bg-[#0A192F] flex items-center justify-center mb-3">
                    <f.icon className="w-5 h-5 text-[#C5A059]" />
                  </span>
                  <p className="font-heading font-semibold text-[#0A192F]">
                    {f.title}
                  </p>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
              Subscription Plans
            </p>
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-[#0A192F]">
              Simple, transparent pricing.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {plansLoading ? (
              <div className="md:col-span-3 flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : plansError ? (
              <p className="md:col-span-3 text-center text-sm text-rose-600">{plansError}</p>
            ) : (
              plans.map((p) => (
                <div
                  key={p.plan_key}
                  data-testid={`plan-${p.plan_key}`}
                  className={`relative p-8 rounded-2xl border ${
                    p.highlight
                      ? "border-[#C5A059] bg-white shadow-xl lg:-translate-y-3"
                      : "border-slate-200 bg-white shadow-sm"
                  }`}
                >
                  {p.highlight && (
                    <span className="absolute top-4 right-4 text-xs tracking-[0.18em] uppercase font-semibold text-[#0A192F] bg-[#C5A059] px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <p className="font-heading text-xl font-semibold text-[#0A192F]">{p.name}</p>
                  <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span
                      data-testid={`plan-${p.plan_key}-price`}
                      className="font-heading text-5xl font-bold text-[#0A192F]"
                    >
                      ${p.monthly_amount_usd}
                    </span>
                    <span className="text-slate-500">/month</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    or ${p.annual_amount_usd.toLocaleString()}/year (save ~{p.annual_savings_pct}%)
                  </p>
                  <ul className="mt-8 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/login">
                    <Button
                      data-testid={`plan-${p.plan_key}-btn`}
                      className={`mt-8 w-full rounded-lg ${
                        p.highlight
                          ? "bg-[#0A192F] hover:bg-[#112A46] text-white"
                          : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
                      }`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              ))
            )}
          </div>
          <p className="mt-10 text-center text-sm text-slate-500">
            All plans require admin approval. Approved attorneys are verified by
            bar number and licensed states.
          </p>
        </div>
      </section>
    </div>
  );
}
